// Right rail: properties for the selection. Rebuilt only when the selected field identity/kind
// changes; otherwise controls re-sync in place so typing never loses focus.
import { updateField } from '../../itf/label.js';
import { S, subscribe, singleSelected, selectedFields, isLocked, isEditorLocked, setLocked, mutate } from '../state.js';
import { $, h } from '../dom.js';
import { KIND_LABEL, FIELD_LETTER, FIELD_ICON_CLASS } from '../icons.js';
import * as B from '../../itf/barcode.js';
import { issuesFor } from '../check.js';
import { Binder, section, row, full, intInput, textInput, message, hint } from './controls.js';
import { geometrySection, multiPanel } from './geometry.js';
import { textSection, fontSection } from './text.js';
import { dateSection } from './date.js';
import { barcodeSection } from './barcode.js';

let binder = null;
let signature = null;

function sigFor() {
  if (!S.label) return 'none';
  const sel = selectedFields();
  if (!sel.length) return 'empty';
  if (sel.length > 1) return `multi:${sel.map((f) => f.id ?? f.key).join(',')}`;
  const f = sel[0];
  return ['one', f.id ?? f.key, f.kind, isLocked(f), f.inverted ? 'inv' : ''].join('|');
}

function makeCtx() {
  const start = singleSelected();
  return {
    hadInverted: !!(start && start.inverted),
    cur: () => singleSelected(),
    edit(patch, tag = null) {
      const f = singleSelected();
      if (!f || isLocked(f)) return;
      try {
        mutate((label) => updateField(label, f.key, patch), { tag: tag ? `prop:${f.id ?? f.key}:${tag}` : null });
        return null;
      } catch (err) {
        // label.js rejects invalid values atomically: show the message next to the control, keep the model.
        if (!err || err.name !== 'LabelError') console.error(err);
        binder && binder.sync();
        return err instanceof Error ? err : new Error(String(err));
      }
    },
  };
}

function emptyPanel() {
  return h(
    'div',
    { class: 'empty' },
    h('div', { style: { margin: 'var(--s5) auto var(--s3)', width: '40px', height: '40px', borderRadius: '50%', background: 'var(--bg-3)', display: 'grid', placeItems: 'center', color: 'var(--ink-3)' }, html: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 3l5 17 2.5-7 7-2.5z"/></svg>' }),
    h('span', { class: 'empty-strong' }, S.label ? 'Nothing selected' : 'No label open'),
    S.label ? 'Click a field on the label or in the list to edit it.' : 'Open a .itf file or start a new label.',
  );
}

function issuesBlock(b) {
  const list = h('ul', { class: 'check-list', style: { marginTop: '10px' } });
  b.add(() => {
    const f = singleSelected();
    const issues = f ? issuesFor(f.key) : [];
    list.hidden = !issues.length;
    list.replaceChildren(
      ...issues.map((i) =>
        h('li', { class: `check-item ${i.severity === 'error' ? 'error' : 'warning'}`, style: { gridTemplateColumns: '1fr' } }, h('span', { class: 'check-msg' }, i.message, i.code ? h('span', { class: 'check-detail' }, i.code) : null)),
      ),
    );
  });
  return list;
}

function singlePanel(b, f) {
  const ctx = makeCtx();
  const cur = ctx.cur;
  const badge = f.kind === 'barcode' ? (B.symbologyById && B.symbologyById(f.symbology)?.label) || f.symbology : KIND_LABEL[f.kind] || f.kind;
  const nameField = textInput(b, { ariaLabel: 'Field name', get: () => cur()?.name, set: (v) => (v.trim() ? ctx.edit({ name: v }, 'name') : new Error('A field needs a name')) });
  const nameInput = nameField.control;
  nameInput.classList.remove('input');
  nameField.style.flex = '1';
  const head = h(
    'div',
    { class: 'props-head' },
    h('div', { class: 'props-name' }, h('div', { class: `field-icon ${FIELD_ICON_CLASS[f.kind] || 'unknown'}`, 'aria-hidden': 'true' }, FIELD_LETTER[f.kind] || '·'), nameField, h('span', { class: 'props-type-badge' }, badge)),
    issuesBlock(b),
  );

  if (f.kind === 'unsupported') {
    nameInput.readOnly = true;
    return h(
      'div',
      null,
      head,
      h(
        'div',
        { class: 'props-body', style: { padding: '0 var(--s4) var(--s4)' } },
        section('Unsupported field', 'unsupported', [
          full(h('span', { class: 'status-unverified' }, 'Unsupported field — kept unchanged')),
          hint(`This field uses ${[f.renderStyle, f.dataSource].filter(Boolean).join(' + ') || 'features'} which this editor does not edit. It stays locked and is saved exactly as it was.`),
          h('div', { class: 'readout' }, `@ ${(f.x / 100).toFixed(2)}, ${(f.y / 100).toFixed(2)} mm · ${f.rotation || 0}°`),
        ]),
      ),
    );
  }

  const locked = isEditorLocked(f);
  const sections = [geometrySection(b, ctx)];
  if (f.kind === 'text') sections.push(textSection(b, ctx), fontSection(b, ctx));
  else if (f.kind === 'date') sections.push(dateSection(b, ctx), fontSection(b, ctx));
  else if (f.kind === 'barcode') sections.push(barcodeSection(b, ctx));

  const lockBtn = h('button', { class: `tog${locked ? ' on' : ''}`, type: 'button', title: 'Editor only — not saved to the file', 'aria-pressed': String(locked) }, locked ? 'Locked' : 'Unlocked');
  lockBtn.addEventListener('click', () => setLocked(cur(), !isEditorLocked(cur())));
  sections.push(
    section(
      'Field',
      'identity',
      [
        row('ID', intInput(b, { ariaLabel: 'Field ID', get: () => cur()?.id, set: (v) => ctx.edit({ id: v }, 'id'), min: 0, max: 999999 })),
        row('Lock', h('div', { class: 'toggle-row' }, lockBtn)),
        hint('Lock is editor only — not saved to the file.'),
      ],
      { collapsed: true },
    ),
  );

  const body = h('fieldset', { disabled: locked, style: { border: '0', margin: '0', padding: '0', minWidth: '0' } }, sections);
  return h(
    'div',
    null,
    head,
    h(
      'div',
      { class: 'props-body', style: { padding: '0 var(--s4) var(--s4)' } },
      locked
        ? h('div', { class: 'hint', style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '12px 0 0' } }, 'Locked in the editor.', h('button', { class: 'btn', type: 'button', onClick: () => setLocked(cur(), false) }, 'Unlock'))
        : null,
      body,
    ),
  );
}

function render(force = false) {
  const root = $('#props-body');
  const sig = sigFor();
  if (!force && sig === signature && binder) {
    binder.sync();
    return;
  }
  signature = sig;
  binder = new Binder();
  const hadFocus = root.contains(document.activeElement);
  const sel = selectedFields();
  let panel;
  if (sig === 'none' || sig === 'empty') panel = emptyPanel();
  else if (sel.length > 1) panel = multiPanel(binder);
  else panel = singlePanel(binder, sel[0]);
  root.replaceChildren(panel);
  if (hadFocus && !root.contains(document.activeElement)) root.focus?.();
}

export function initProps() {
  subscribe((r) => {
    if (r.has('doc') || r.has('selection') || r.has('issues')) render();
  });
  document.addEventListener('labels:focus-content', () => {
    render();
    if (S.rightCollapsed) return;
    const el = $('#props-body [data-primary="true"]');
    if (el) {
      el.focus();
      if (el.select) el.select();
    }
  });
  render(true);
}

export { message };
